'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  createCourseService,
  deleteCourseService,
  getCourseExportService,
  getCourseHandoutService,
  linkUnitToCourseService,
  publishCourseService,
  unlinkUnitFromCourseService,
  updateCourseService,
  validateCourseService,
  validateLanguageUrls,
  validateUnitService,
} from '@/services/httpServices/CourseServices';
import type {
  CourseByIdResponse,
  CourseListResponse,
  CreateCourseServiceConfig,
  DeleteCourseServiceConfig,
  GetCourseByIdServiceConfig,
  GetCourseExportServiceConfig,
  GetCourseHandoutServiceConfig,
  LinkUnitToCourseServiceConfig,
  ReorderCoursesServiceConfig,
  UnlinkUnitFromCourseServiceConfig,
  UpdateCourseServiceConfig,
  ValidateUnitResponse,
  ValidateUnitServiceConfig,
} from '@/services/httpServices/CourseServices/types';
import { createDeeplinkService } from '@/services/httpServices/GeneralServices';
import type { CreateDeeplinkBody, CreateDeeplinkServiceConfig } from '@/services/httpServices/GeneralServices/types';
import type { HttpServiceConfig } from '@/services/httpServices/types';

import { courseByIdKeys, courseLinkableKeys, courseListKeys, courseValidationKeys, unitValidationKeys } from '../queries/courseQueryKeys';

/**
 * Switches the in-form language picker to a new language and invalidates the
 * course-by-id query so the form refetches in that language slot. Mirrors
 * `useSequenceLanguageChange` / `useQuizLanguageChange`.
 */
export function useCourseLanguageChange() {
  const queryClient = useQueryClient();

  const changeLanguage = useCallback(
    async (
      courseQueryParams: GetCourseByIdServiceConfig | undefined,
      newLanguage: string,
    ): Promise<void> => {
      if (courseQueryParams === undefined || courseQueryParams.params === undefined) {
        return;
      }

      const updatedParams: GetCourseByIdServiceConfig = {
        ...courseQueryParams,
        params: {
          ...courseQueryParams.params,
          params: {
            ...courseQueryParams.params.params,
            spaceId: courseQueryParams.params.params?.spaceId ?? '',
            id: courseQueryParams.params.params?.id ?? '',
            courseLanguage: newLanguage,
          },
          query: {
            ...courseQueryParams.params.query,
            language: newLanguage,
          },
        },
      };

      await queryClient.invalidateQueries({
        queryKey: courseByIdKeys(updatedParams),
      });
    },
    [queryClient],
  );

  return { changeLanguage };
}

/**
 * Mutation hook for creating a new course with language URL support
 */
export function useCreateCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateCourseServiceConfig) => {
      try {
        // Note: Create course only supports basic fields (title)
        // Language URLs are only available in update operations
        return await createCourseService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to create course',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate all course list queries using hierarchical keys
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
      });

      // No need to invalidate a specific course by ID since this is a new course
    },
  });
}

/**
 * Mutation hook for updating an existing course with language URL support
 */
export function useUpdateCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateCourseServiceConfig) => {
      try {
        // Validate language URLs before API call
        if (config.params?.body.videoUrls !== undefined) {
          const videoUrlValidation = validateLanguageUrls(config.params.body.videoUrls);
          if (videoUrlValidation.valid === false) {
            throw new Error(`Video URL validation failed: ${videoUrlValidation.errors.join(', ')}`);
          }
        }

        if (config.params?.body.surveyUrls !== undefined) {
          const surveyUrlValidation = validateLanguageUrls(config.params.body.surveyUrls);
          if (surveyUrlValidation.valid === false) {
            throw new Error(`Survey URL validation failed: ${surveyUrlValidation.errors.join(', ')}`);
          }
        }

        const response = await updateCourseService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate all course list queries using hierarchical keys
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
      });

      // Invalidate the specific course by ID query
      await queryClient.invalidateQueries({
        queryKey: courseByIdKeys(variables),
      });
    },
  });
}

/**
 * Mutation hook for deleting a course
 */
export function useDeleteCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DeleteCourseServiceConfig) => {
      try {
        await deleteCourseService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Only invalidate course list queries - don't invalidate the specific course
      // that was just deleted, as this causes unnecessary refetches
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
      });
    },
  });
}

/**
 * Mutation hook for linking a unit to a course
 */
export function useLinkUnitToCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: LinkUnitToCourseServiceConfig) => {
      try {
        const response = await linkUnitToCourseService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate linkable courses as this course may no longer be linkable
      await queryClient.invalidateQueries({
        queryKey: courseLinkableKeys(),
      });

      // Invalidate course list to reflect linking changes
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
      });
    },
  });
}

/**
 * Mutation hook for unlinking a unit from a course
 */
export function useUnlinkUnitFromCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UnlinkUnitFromCourseServiceConfig) => {
      try {
        const response = await unlinkUnitFromCourseService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate linkable courses as this course may now be linkable again
      await queryClient.invalidateQueries({
        queryKey: courseLinkableKeys(),
      });

      // Invalidate course list to reflect unlinking changes
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
      });
    },
  });
}

/**
 * Mutation hook for reordering courses with optimistic updates
 */
export function useReorderCoursesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: ReorderCoursesServiceConfig & { reorderedNodes?: CourseListResponse['nodes'] }) => {
      try {
        const { reorderCoursesService } = await import('@/services/httpServices/CourseServices');
        // Extract the actual service config without the reorderedNodes
        const { reorderedNodes, ...serviceConfig } = config;
        const response = await reorderCoursesService(serviceConfig);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic updates
      // Use hierarchical keys for better cache targeting
      await queryClient.cancelQueries({
        queryKey: courseListKeys(),
      });

      // Get all course list queries for optimistic updates using hierarchical keys
      const queriesData = queryClient.getQueriesData<CourseListResponse>({
        queryKey: courseListKeys(),
      });

      const previousQueries: Array<{
        queryKey: readonly unknown[];
        data: CourseListResponse;
      }> = [];

      // Apply optimistic updates to all matching course list queries
      queriesData.forEach(([queryKey, data]) => {
        // TypeScript now knows data is CourseListResponse | undefined
        if (data?.nodes !== undefined && Array.isArray(data.nodes) === true && data?.pageInfo !== undefined) {
          // Store previous data for potential rollback
          previousQueries.push({ queryKey, data });

          // Apply optimistic update with reordered data
          const updatedData: CourseListResponse = variables.reorderedNodes !== undefined
            ? { ...data, nodes: variables.reorderedNodes }
            : data;

          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { previousQueries };
    },
    onSuccess: async () => {
      // Invalidate and refetch all course list queries after successful reorder
      // Use hierarchical keys for precise cache invalidation
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
        refetchType: 'active',
      });
    },
    onError: (err, _variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousQueries !== undefined) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      // Log error for debugging
      console.error('Course reorder failed:', err);
    },
  });
}

/**
 * Mutation hook for publishing a course with all its units
 * Uses the PUBLISH_ALL_UNITS additional action with language URL support
 */
export function usePublishCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateCourseServiceConfig) => {
      try {
        // Validate language URLs before publishing
        if (config.params?.body.videoUrls !== undefined) {
          const videoUrlValidation = validateLanguageUrls(config.params.body.videoUrls);
          if (videoUrlValidation.valid === false) {
            throw new Error(`Video URL validation failed: ${videoUrlValidation.errors.join(', ')}`);
          }
        }

        if (config.params?.body.surveyUrls !== undefined) {
          const surveyUrlValidation = validateLanguageUrls(config.params.body.surveyUrls);
          if (surveyUrlValidation.valid === false) {
            throw new Error(`Survey URL validation failed: ${surveyUrlValidation.errors.join(', ')}`);
          }
        }

        const response = await publishCourseService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to publish course',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate course-related queries
      await queryClient.invalidateQueries({
        queryKey: courseListKeys(),
      });
      await queryClient.invalidateQueries({
        queryKey: courseByIdKeys(variables),
      });

      // Invalidate unit validation queries using partial matching - clean and simple
      // This follows the established pattern but is optimized to avoid excessive iterations
      await queryClient.invalidateQueries({
        queryKey: ['unitValidation'],
        exact: false,
      });
    },
  });
}

/**
 * Mutation hook for validating a course structure
 * Checks if the course meets all requirements
 */
export function useValidateCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: GetCourseByIdServiceConfig) => {
      try {
        const response = await validateCourseService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to validate course',
        );
      }
    },
    onSuccess: async (data, variables) => {
      // Cache the validation result using proper query keys
      queryClient.setQueryData(courseValidationKeys(variables), data);

      // Also update the course by ID cache if validation data is included
      await queryClient.invalidateQueries({
        queryKey: courseByIdKeys(variables),
      });
    },
  });
}

/**
 * Mutation hook for validating a unit within a course context
 * Checks if a unit meets course requirements
 */
export function useValidateUnitInCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: ValidateUnitServiceConfig) => {
      try {
        const response = await validateUnitService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to validate unit',
        );
      }
    },
    onSuccess: async (data, variables) => {
      // Cache the validation result using proper query keys
      queryClient.setQueryData(unitValidationKeys(variables), data);

      // Invalidate the specific course by ID queries to get fresh validation data
      await queryClient.invalidateQueries({
        queryKey: courseByIdKeys(),
      });
    },
  });
}

/**
 * Hook to get cached unit validation result
 */
export function useUnitValidationCache(config: ValidateUnitServiceConfig) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<ValidateUnitResponse>(unitValidationKeys(config));
}

/**
 * Hook to get cached course validation result
 */
export function useCourseValidationCache(config: GetCourseByIdServiceConfig) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<CourseByIdResponse>(courseValidationKeys(config));
}

/** Params for creating a content deeplink */
type ContentDeeplinkParams = {
  httpRequestConfig: HttpServiceConfig;
} & CreateDeeplinkBody;

/**
 * Mutation hook for creating a content deeplink
 * Generates a shareable link for a course or sequence
 */
export function useContentDeeplinkMutation() {
  return useMutation({
    mutationFn: async ({
      contentId,
      sourceLanguage,
      httpRequestConfig,
      loadDraftVersion = false,
      contentType,
    }: ContentDeeplinkParams) => {
      try {
        const config: CreateDeeplinkServiceConfig = {
          httpRequestConfig,
          params: {
            body: {
              contentId,
              loadDraftVersion,
              contentType,
              sourceLanguage,
            },
          },
        };
        const response = await createDeeplinkService(config);
        return response.data.url;
      } catch (error) {
        console.error('[useContentDeeplinkMutation] failed', {
          contentId,
          contentType,
          sourceLanguage,
          loadDraftVersion,
          error,
        });
        throw error instanceof Error
          ? error
          : new Error('Failed to create deeplink');
      }
    },
  });
}

/**
 * @deprecated Use useContentDeeplinkMutation instead
 */
export const useCourseDeeplinkMutation = useContentDeeplinkMutation;

/**
 * Mutation hook for downloading course handout
 * Returns a URL for downloading the course handout PDF
 */
export function useCourseHandoutMutation() {
  return useMutation({
    mutationFn: async (config: GetCourseHandoutServiceConfig) => {
      try {
        const response = await getCourseHandoutService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to get course handout',
        );
      }
    },
  });
}

/**
 * Mutation hook for exporting course content as XLSX
 * Returns a URL for downloading the course export file
 */
export function useCourseExportMutation() {
  return useMutation({
    mutationFn: async (config: GetCourseExportServiceConfig) => {
      try {
        const response = await getCourseExportService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to export course content',
        );
      }
    },
  });
}
